import { Center, Flex, Image, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import emptyShapes from 'assets/images/shapes_empty.png';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectActiveWidgetMenu } from 'modules/Editor/store/editorSettingsSelector';
import { setImagePanelTabIndex } from 'modules/Editor/store/editorSettingsSlice';
import { ReactElement, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeGrid as Grid } from 'react-window';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';

import { COLUMN_NUM, GUTTER_SIZE, THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../InfiniteGrid/InfiniteGrid.config';
import DeleteUploadImageAlertDialog from './DeleteUploadImageAlertDialog';
import { UserImagesProps } from './UploadMenu.helper';
import { UserImageCell } from './UserImageCell';

export const UserImages = ({ uploadImages, disableDragDrop }: UserImagesProps): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.imagesMenu' });
  const userImagesContainerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const { isOpen, onOpen: onOpenDeleteAlertDialog, onClose: onCloseDeleteAlertDialog } = useDisclosure();
  const [deleteMediaId, setDeleteMediaId] = useState('');
  const dispatch = useAppDispatch();
  const { venngageE2 } = useVenngageE1Gate();
  const isInCombineUploadAndImageExperiment = venngageE2?.experiments?.combineUploadAndImagesPanelExp === true;
  const activeWidgetMenu = useAppSelector(selectActiveWidgetMenu);

  const onResetDeleteMediaId = () => setDeleteMediaId('');

  // Update the infinite scroll box size when the window is resizing
  useResizeObserver({
    ref: userImagesContainerRef,
    onResize: ({ width = 0, height = 0 }) => {
      setContainerSize({ width, height });
    },
  });

  const uploadImagesData = {
    uploadImages,
    onOpenDeleteAlertDialog,
    setDeleteMediaId,
    disableDragDrop,
  };

  const openImageLibrary = useCallback(() => {
    dispatch(setImagePanelTabIndex(1));
  }, [dispatch]);

  return (
    <Flex
      ref={userImagesContainerRef}
      flexDir='column'
      h='1000'
      mt='3'
      data-testid='user-images-container'
      overflowY='hidden'
    >
      {!!uploadImages ? (
        <>
          {uploadImages.length > 0 ? (
            <>
              <Grid
                itemData={uploadImagesData}
                columnCount={COLUMN_NUM}
                columnWidth={GUTTER_SIZE + THUMBNAIL_WIDTH}
                rowHeight={GUTTER_SIZE + THUMBNAIL_HEIGHT}
                height={containerSize.height}
                width={containerSize.width}
                rowCount={Math.ceil(uploadImages.length / COLUMN_NUM)}
                data-testid='upload-images-grid'
              >
                {UserImageCell}
              </Grid>
              <DeleteUploadImageAlertDialog
                isOpen={isOpen}
                onClose={onCloseDeleteAlertDialog}
                onResetId={onResetDeleteMediaId}
                mediaId={deleteMediaId}
              />
            </>
          ) : (
            <Center minH='300px'>
              <Flex flexDirection='column' alignItems='center' gap='3'>
                <Image src={emptyShapes} w='50px' h='48px' role='presentation' flexShrink='0' />
                <Flex flexDirection='column' justifyContent='center' alignItems={'center'}>
                  <Text fontSize='sm' fontWeight='medium' color='var(--vg-colors-gray-600)'>
                    {t('emptyAssets')}
                  </Text>
                  <Text fontSize='xs' fontWeight='normal' color='var(--vg-colors-font-400)'>
                    {!isInCombineUploadAndImageExperiment || activeWidgetMenu === WIDGET_MENU_OPTIONS.REPLACE ? (
                      <>{t('emptyAssetsDescription')}</>
                    ) : (
                      <>
                        {t('uploadImages')}{' '}
                        <Text
                          as='button'
                          color='var(--vg-colors-upgrade-500)'
                          onClick={openImageLibrary}
                          cursor='pointer'
                        >
                          {t('searchImage')}
                        </Text>
                      </>
                    )}
                  </Text>
                </Flex>
              </Flex>
            </Center>
          )}
        </>
      ) : (
        <Center>
          <Spinner data-testid='upload-images-spinner' />
        </Center>
      )}
    </Flex>
  );
};
